/**
 * Parsing helpers for the keyed attribute envelope — `{ <key>: {...} }` with
 * negation as a `{ not: ... }` wrapper. Shared by every leaf that uses that
 * shape (`attribute`, `device_attribute`), and independent of which key or
 * which surface: each format adapter still owns its own serializer.
 */

import { isRecord } from '@/utils/objects';
import { isTwoValueFilterOperator, isUnaryFilterOperator, type AttributeFilterState } from './attribute-filter-state';

export type WireAttribute = {
  field?: unknown;
  operator?: unknown;
  value?: unknown;
  value2?: unknown;
  inverse?: unknown;
  type_id?: unknown;
};

function keyedAttributeProperty(json: unknown, key: string): WireAttribute | null {
  if (!isRecord(json)) {
    return null;
  }
  const attribute = json[key];
  return isRecord(attribute) ? attribute : null;
}

/**
 * Unwraps `{ <key>: {...} }` or `{ not: { <key>: {...} } }` into the inner
 * attribute payload plus negation flag, or null if the node is any other
 * shape (segment filters, and/or groups, nested nots...).
 */
export function unwrapAttributeEnvelope(
  json: unknown,
  key: string,
): { attribute: WireAttribute; negated: boolean } | null {
  const direct = keyedAttributeProperty(json, key);
  if (direct != null) {
    return { attribute: direct, negated: false };
  }
  const negated = isRecord(json) ? keyedAttributeProperty(json.not, key) : null;
  if (negated != null) {
    return { attribute: negated, negated: true };
  }
  return null;
}

function uiOperator(attribute: WireAttribute, negated: boolean): string {
  const operator = String(attribute.operator ?? '');
  return negated ? `!${operator}` : operator;
}

/**
 * The shape checks every attribute-shaped envelope shares: unwraps the keyed
 * node, requires string field/operator, and declines the legacy `inverse`
 * boolean spelling — the backend's proto parser accepts negation as either the
 * `{ not: ... }` wrapper or an `inverse` flag inside the block, but row state
 * models only the wrapper form (as the '!' operator prefix), so a leaf flagged
 * `inverse: true` rides the error-node passthrough with the key intact instead
 * of losing it on save. Returns the payload plus the '!'-prefixed UI operator.
 */
export function unwrapSupportableAttributeEnvelope(
  json: unknown,
  key: string,
): { attribute: WireAttribute; operator: string } | null {
  const unwrapped = unwrapAttributeEnvelope(json, key);
  if (unwrapped == null) {
    return null;
  }
  const { attribute, negated } = unwrapped;
  if (typeof attribute.field !== 'string' || typeof attribute.operator !== 'string' || attribute.inverse === true) {
    return null;
  }
  return { attribute, operator: uiOperator(attribute, negated) };
}

// Parses an unwrapped attribute-shaped envelope into filter state. Call only
// after the surface's support guard — throws on other shapes. Under dryad's
// plugin contract a throw escaping a plugin's `parse` is a plugin bug, so
// plugins gate on their guard and decline with `null` before calling this.
export function parseAttributeEnvelope(json: unknown, key: string): AttributeFilterState {
  const unwrapped = unwrapAttributeEnvelope(json, key);
  if (unwrapped == null) {
    throw new Error(`Not a ${key} filter: ${JSON.stringify(json)}`);
  }
  const { attribute, negated } = unwrapped;
  const operator = uiOperator(attribute, negated);
  return {
    field: String(attribute.field ?? ''),
    operator,
    value: isUnaryFilterOperator(operator) || attribute.value == null ? null : String(attribute.value),
    value2: isTwoValueFilterOperator(operator) && attribute.value2 != null ? String(attribute.value2) : null,
  };
}
